/**
 * Discount link banner.
 *
 * Shows a slim bar when a visitor arrives through a shareable discount link such as
 * /discount/30STAFF?redirect=/products/evertee-original®
 *
 * The code is detected, in order of confidence:
 *   1. a ?discount= / ?discount_code= query parameter on the landing URL
 *   2. the /discount/CODE path itself (before Shopify redirects)
 *   3. Shopify's discount_code cookie
 *   4. the codes attached to the cart (/cart.js), once something has been added
 *
 * Once found, the code is remembered for the rest of the browsing session so the bar
 * stays put while the visitor moves around the store with an empty cart.
 */
(function initDiscountLinkBanner() {
  const banners = document.querySelectorAll('[data-discount-banner]');
  if (!banners.length) return;

  const STORAGE_KEY = 'evertee_discount_link_code';
  const DISMISS_KEY = 'evertee_discount_link_dismissed';

  // Every placement is rendered from the same settings, so one map covers them all.
  let codeMessages = {};
  try {
    codeMessages = JSON.parse(banners[0].dataset.discountBannerCodes || '{}');
  } catch (_) {
    codeMessages = {};
  }

  if (!Object.keys(codeMessages).length) return;

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function isKnown(value) {
    const code = normalise(value);
    return code && Object.prototype.hasOwnProperty.call(codeMessages, code) ? code : null;
  }

  function readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  }

  function codeFromUrl() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (_) {
      return null;
    }

    const candidates = [params.get('discount'), params.get('discount_code'), params.get('code')];
    for (const candidate of candidates) {
      const code = isKnown(candidate);
      if (code) return code;
    }
    return null;
  }

  function codeFromPath() {
    const match = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
    if (!match) return null;
    try {
      return isKnown(decodeURIComponent(match[1]));
    } catch (_) {
      return isKnown(match[1]);
    }
  }

  function codeFromCookie() {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const cookie of cookies) {
      const separator = cookie.indexOf('=');
      if (separator === -1) continue;
      const name = cookie.slice(0, separator).trim();
      if (name !== 'discount_code' && name !== 'discount') continue;
      try {
        const code = isKnown(decodeURIComponent(cookie.slice(separator + 1).trim()));
        if (code) return code;
      } catch (_) {}
    }
    return null;
  }

  async function codeFromCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return null;
      const cart = await response.json();

      const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      for (const entry of discountCodes) {
        const code = isKnown(typeof entry === 'string' ? entry : entry && entry.code);
        if (code) return code;
      }

      const applications = Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];
      for (const application of applications) {
        const code = isKnown(application && (application.code || application.title));
        if (code) return code;
      }
    } catch (_) {}
    return null;
  }

  function cleanUpUrl() {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      let changed = false;
      ['discount', 'discount_code', 'code'].forEach((key) => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch (_) {}
  }

  function show(code) {
    // A banner may already be rendered visible server side, so honour a dismissal here too.
    if (readSession(DISMISS_KEY) === code) {
      hide();
      return;
    }
    banners.forEach((banner) => {
      const messageEl = banner.querySelector('[data-discount-banner-message]');
      if (messageEl) messageEl.textContent = codeMessages[code];
      banner.classList.add('is-visible');
    });
  }

  function hide() {
    banners.forEach((banner) => banner.classList.remove('is-visible'));
  }

  let activeCode = null;

  function activate(code, options) {
    if (!code || code === activeCode) return;
    activeCode = code;
    writeSession(STORAGE_KEY, code);
    if (options && options.fromUrl) cleanUpUrl();
    show(code);
  }

  // Dismissing the top bar hides every placement for the rest of the session.
  document.querySelectorAll('[data-discount-banner-close]').forEach((closeButton) => {
    closeButton.addEventListener('click', () => {
      if (activeCode) writeSession(DISMISS_KEY, activeCode);
      hide();
    });
  });

  const urlCode = codeFromUrl();
  if (urlCode) {
    activate(urlCode, { fromUrl: true });
  } else {
    const syncCode = codeFromPath() || codeFromCookie() || isKnown(readSession(STORAGE_KEY));
    if (syncCode) activate(syncCode);
  }

  // The cart only reports discount codes once it holds an item, so confirm asynchronously.
  function refreshFromCart() {
    codeFromCart().then((code) => {
      if (code) activate(code);
    });
  }

  refreshFromCart();
  document.addEventListener('cart:updated', refreshFromCart);
  document.addEventListener('cart:refresh', refreshFromCart);
})();
